/* CYME CUSTOM START: consent-aware Klaviyo runtime and resilient embeds.
   Klaviyo's Shopify app embed is deliberately disabled in settings_data.json.
   This controller installs the same documented Klaviyo.js endpoint only when
   Shopify permits marketing processing and its privacy UI is closed, keeping
   the vendor runtime off the cold mobile critical path and preventing two
   independent modals from stacking. The existing native forms remain the
   fail-open path when Klaviyo is delayed or blocked. */
(() => {
  if (window.__cymeKlSignup) {
    return;
  }

  window.__cymeKlSignup = true;

  const controllerScript = document.currentScript;
  const publicKey = controllerScript?.dataset.cymeKlaviyoPublicKey || 'ReSSdK';
  const customerEmail = controllerScript?.dataset.cymeKlaviyoCustomerEmail || '';
  const wrapperSelector = '[data-cyme-kl-signup]';
  const privacyUiSelector = '#shopify-pc__banner, #shopify-pc__prefs, #shopify-pc__prefs__dialog, .shopify-pc__prefs__dialog';
  const privacyControlSelector = '#shopify-pc__banner__btn-manage-prefs, #shopify-pc__prefs button, [data-shopify-pc-button], a[href="#shopify-customer-privacy"]';
  const timing = Object.freeze({
    nearbyLoad: 250,
    backgroundLoad: 7000,
    idleTimeout: 1500,
    idleFallback: 120,
    embedFallback: 3000,
    privacyDiscovery: [150, 600, 1800],
    privacyControl: [0, 100, 500],
    consentSettle: [0, 250, 1000]
  });
  const states = new WeakMap();
  const watchedWrappers = new WeakSet();
  const watchedPrivacyNodes = new WeakSet();
  const html = document.documentElement;

  let runtimeState = 'waiting-consent';
  let runtimeEligible = false;
  let nearSignup = false;
  let waitingForWindowLoad = false;
  let loadTimer = 0;
  let idleHandle = 0;
  let productViewQueued = false;

  const setRuntimeState = (state) => {
    runtimeState = state;
    html.dataset.cymeKlaviyoRuntime = state;
  };

  setRuntimeState(runtimeState);

  const isVisible = (element) => {
    if (!element || element.hidden) {
      return false;
    }

    const style = window.getComputedStyle(element);

    if (style.display === 'none' || style.visibility === 'hidden') {
      return false;
    }

    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };

  const syncPrivacyUi = () => {
    const isOpen = Array.from(document.querySelectorAll(privacyUiSelector)).some(isVisible);
    document.body?.classList.toggle('cyme-privacy-ui-open', isOpen);
    return isOpen;
  };

  const bindPrivacyNodes = () => {
    document.querySelectorAll(privacyUiSelector).forEach((node) => {
      if (watchedPrivacyNodes.has(node) || !('MutationObserver' in window)) {
        return;
      }

      const observer = new MutationObserver(syncPrivacyUi);
      observer.observe(node, {
        attributes: true,
        attributeFilter: ['aria-hidden', 'class', 'hidden', 'open', 'style']
      });
      watchedPrivacyNodes.add(node);
    });

    syncPrivacyUi();
  };

  const privacyApi = () => window.Shopify?.customerPrivacy;

  const marketingIsAllowed = () => {
    const api = privacyApi();

    if (!api || typeof api.marketingAllowed !== 'function' || syncPrivacyUi()) {
      return false;
    }

    try {
      // `shouldShowBanner()` is a presentation hint, not a processing
      // permission. In Shopify's own returning-visitor state it can remain
      // true after an explicit `marketing: yes` decision, which previously
      // stranded Klaviyo in `waiting-consent`. The Allowed method combines
      // the visitor's decision, merchant configuration and region, and also
      // keeps the runtime unloaded after an explicit marketing decline.
      return api.marketingAllowed() === true;
    } catch (_error) {
      return false;
    }
  };

  const runWhenIdle = (callback) => {
    if ('requestIdleCallback' in window) {
      idleHandle = window.requestIdleCallback(callback, { timeout: timing.idleTimeout });
    } else {
      idleHandle = window.setTimeout(callback, timing.idleFallback);
    }
  };

  const clearScheduledLoad = () => {
    window.clearTimeout(loadTimer);

    if ('cancelIdleCallback' in window) {
      window.cancelIdleCallback(idleHandle);
    } else {
      window.clearTimeout(idleHandle);
    }

    loadTimer = 0;
    idleHandle = 0;
  };

  const prepareKlaviyoProxy = () => {
    if (window.klaviyo) {
      return;
    }

    window._klOnsite = window._klOnsite || [];

    try {
      window.klaviyo = new Proxy({}, {
        get: (_target, property) => {
          if (property === 'push') {
            return (...args) => window._klOnsite.push(...args);
          }

          return (...args) => {
            const callback = typeof args[args.length - 1] === 'function' ? args.pop() : null;

            return new Promise((resolve) => {
              window._klOnsite.push([
                property,
                ...args,
                (value) => {
                  callback?.(value);
                  resolve(value);
                }
              ]);
            });
          };
        }
      });
    } catch (_error) {
      window.klaviyo = [];
      window.klaviyo.push = (...args) => window._klOnsite.push(...args);
    }
  };

  const identifyCustomerWhenPermitted = () => {
    if (!customerEmail || typeof window.klaviyo?.identify !== 'function') {
      return;
    }

    const api = privacyApi();

    try {
      // Shopify's Allowed methods combine region, merchant configuration and
      // the visitor's decision. currentVisitorConsent alone omits that context.
      if (!api || api.marketingAllowed() !== true) {
        return;
      }
    } catch (_error) {
      return;
    }

    const identification = window.klaviyo.identify({ $email: customerEmail });
    identification?.catch?.(() => {});
  };

  const markRuntimeReady = () => {
    setRuntimeState('ready');
    identifyCustomerWhenPermitted();
    trackProductViewWhenPermitted();
    document.dispatchEvent(new CustomEvent('cyme:klaviyo-ready'));
  };

  // The disabled app embed normally owns these two product-view calls.
  // Keep their documented manual equivalent in this single integration.
  const trackProductViewWhenPermitted = () => {
    const metadata = controllerScript?.dataset.cymeKlaviyoProduct;

    if (productViewQueued || !metadata || runtimeState !== 'ready' || !marketingIsAllowed()) {
      return;
    }

    try {
      if (privacyApi()?.analyticsProcessingAllowed?.() !== true) {
        return;
      }

      const item = JSON.parse(metadata);

      if (!item?.ProductID || typeof item.Name !== 'string' || !Number.isFinite(item.Price) || item.Price < 0 || !/^[A-Z]{3}$/.test(item.Currency)) {
        return;
      }

      // Reserve before the provider call: a rejected/ambiguous result is not
      // proof that the event was not received and must not cause a duplicate.
      productViewQueued = true;
      window.klaviyo.track('Viewed Product', item)?.catch?.(() => {});
      window.klaviyo.trackViewedItem({
        Title: item.Name,
        ItemId: item.ProductID,
        Categories: item.Categories,
        ImageUrl: item.ImageURL,
        Url: item.URL,
        Metadata: { Brand: item.Brand, Price: item.Price, CompareAtPrice: item.CompareAtPrice, Currency: item.Currency }
      })?.catch?.(() => {});
    } catch (_error) {
      // Tracking availability must not break the native storefront/forms.
    }
  };

  const loadRuntime = () => {
    clearScheduledLoad();

    if (runtimeState === 'loading' || runtimeState === 'ready' || runtimeState === 'app-embed-present') {
      return;
    }

    if (!marketingIsAllowed()) {
      runtimeEligible = false;
      setRuntimeState('waiting-consent');
      return;
    }

    const existingScript = document.querySelector('script[src*="static.klaviyo.com/onsite/js/"][src*="klaviyo.js"]');

    if (existingScript) {
      setRuntimeState('app-embed-present');
      return;
    }

    prepareKlaviyoProxy();
    setRuntimeState('loading');

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://static.klaviyo.com/onsite/js/${encodeURIComponent(publicKey)}/klaviyo.js`;
    script.dataset.cymeConsentAware = 'true';
    script.addEventListener('load', markRuntimeReady, { once: true });
    script.addEventListener('error', () => setRuntimeState('failed'), { once: true });
    document.head.appendChild(script);
  };

  const scheduleRuntime = (expedite = false) => {
    if (!runtimeEligible || runtimeState === 'loading' || runtimeState === 'ready' || runtimeState === 'app-embed-present') {
      return;
    }

    if (!marketingIsAllowed()) {
      runtimeEligible = false;
      clearScheduledLoad();
      setRuntimeState('waiting-consent');
      return;
    }

    if (document.readyState !== 'complete') {
      if (!waitingForWindowLoad) {
        waitingForWindowLoad = true;
        setRuntimeState('waiting-window-load');
        window.addEventListener('load', () => {
          waitingForWindowLoad = false;
          scheduleRuntime(nearSignup);
        }, { once: true });
      }

      return;
    }

    const shouldExpedite = expedite || nearSignup || window.Shopify?.designMode;
    const delay = shouldExpedite ? timing.nearbyLoad : timing.backgroundLoad;

    if (loadTimer && !shouldExpedite) {
      return;
    }

    clearScheduledLoad();
    setRuntimeState(shouldExpedite ? 'scheduled-nearby' : 'scheduled-idle');
    loadTimer = window.setTimeout(() => runWhenIdle(loadRuntime), delay);
  };

  const armRuntime = () => {
    bindPrivacyNodes();

    if (!marketingIsAllowed()) {
      runtimeEligible = false;
      clearScheduledLoad();

      // A consent change does not unload a script that has already started.
      if (runtimeState !== 'loading' && runtimeState !== 'ready' && runtimeState !== 'app-embed-present') {
        setRuntimeState('waiting-consent');
      }

      return;
    }

    runtimeEligible = true;
    trackProductViewWhenPermitted();
    scheduleRuntime(nearSignup);
  };

  const runtimeIntersection = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }

        nearSignup = true;
        scheduleRuntime(true);
      }, { rootMargin: '600px 0px', threshold: 0 })
    : null;

  const watchRuntimeTrigger = (wrapper) => {
    if (watchedWrappers.has(wrapper)) {
      return;
    }

    watchedWrappers.add(wrapper);
    runtimeIntersection?.observe(wrapper);
  };

  const reconcile = (wrapper, final = false) => {
    if (!wrapper.isConnected) {
      return;
    }

    const embed = wrapper.querySelector('.cyme-kl-signup__embed');
    const fallback = wrapper.querySelector('.cyme-kl-signup__fallback');

    if (wrapper.dataset.cymeEngaged) {
      return;
    }

    // Native form submissions return their success/error banner inside the
    // fallback. That state belongs to the visitor even if Klaviyo is available.
    if (fallback?.querySelector('.banner')) {
      fallback.hidden = false;
      wrapper.dataset.cymeEngaged = '1';
      wrapper.classList.remove('is-live');
      wrapper.classList.add('is-fallback');
      return;
    }

    if (embed?.childElementCount > 0) {
      if (fallback) {
        fallback.hidden = true;
      }

      wrapper.classList.remove('is-fallback');
      wrapper.classList.add('is-live');
      return;
    }

    if (final) {
      if (fallback) {
        fallback.hidden = false;
      }

      wrapper.classList.remove('is-live');
      wrapper.classList.add(fallback ? 'is-fallback' : 'is-live');
    }
  };

  const bind = (wrapper) => {
    watchRuntimeTrigger(wrapper);

    if (states.has(wrapper)) {
      reconcile(wrapper);
      return;
    }

    const embed = wrapper.querySelector('.cyme-kl-signup__embed');
    const fallback = wrapper.querySelector('.cyme-kl-signup__fallback');
    const state = { observer: null, fallbackTimer: 0 };

    if (fallback) {
      const markEngaged = () => {
        wrapper.dataset.cymeEngaged = '1';
      };

      fallback.addEventListener('focusin', markEngaged);
      fallback.addEventListener('input', markEngaged);
    }

    if (embed && 'MutationObserver' in window) {
      state.observer = new MutationObserver(() => reconcile(wrapper));
      state.observer.observe(embed, { childList: true });
    }

    state.fallbackTimer = window.setTimeout(() => reconcile(wrapper, true), timing.embedFallback);
    states.set(wrapper, state);
    reconcile(wrapper);
  };

  const wrappersWithin = (root) => {
    const wrappers = [];

    if (root instanceof Element && root.matches(wrapperSelector)) {
      wrappers.push(root);
    }

    root.querySelectorAll?.(wrapperSelector).forEach((wrapper) => wrappers.push(wrapper));
    return wrappers;
  };

  const init = (root = document) => {
    wrappersWithin(root).forEach(bind);
  };

  const disconnect = (root) => {
    wrappersWithin(root).forEach((wrapper) => {
      const state = states.get(wrapper);

      if (state) {
        state.observer?.disconnect();
        window.clearTimeout(state.fallbackTimer);
        states.delete(wrapper);
      }

      runtimeIntersection?.unobserve(wrapper);
      watchedWrappers.delete(wrapper);
    });
  };

  init();
  bindPrivacyNodes();

  timing.privacyDiscovery.forEach((delay) => window.setTimeout(bindPrivacyNodes, delay));

  document.addEventListener('click', (event) => {
    if (!event.target.closest?.(privacyControlSelector)) {
      return;
    }

    timing.privacyControl.forEach((delay) => window.setTimeout(bindPrivacyNodes, delay));
  }, true);

  document.addEventListener('visitorConsentCollected', () => {
    timing.consentSettle.forEach((delay) => window.setTimeout(armRuntime, delay));
  });

  window.addEventListener('klaviyoForms', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => disconnect(event.target));

  if (typeof privacyApi()?.marketingAllowed === 'function') {
    armRuntime();
  } else if (window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures(
      [
        {
          name: 'consent-tracking-api',
          version: '0.1'
        }
      ],
      (error) => {
        if (!error) {
          armRuntime();
        }
      }
    );
  }
})();
/* CYME CUSTOM END: consent-aware Klaviyo runtime and resilient embeds. */
